import { z } from 'zod';

import { parseLocaleNumber } from '@/utils';

const isEmptyOrOnlyPlusOrMinus = (val: string | undefined) => {
  if (!val) return true; // allow empty value

  return val?.trim() === '' || val?.trim() === '+' || val?.trim() === '-';
};

const isOnlyPlusOrMinus = (val: string | undefined) => {
  if (!val) return false;
  return val?.trim() === '+' || val?.trim() === '-';
};

/**
 * Validates a required string input as a positive number (> 0).
 * Supports locale-aware decimal notation (comma or period separator).
 */
export const requiredPositiveNumber = () => {
  return z
    .string()
    .min(1, 'validationErrors.Required')
    .refine((val) => {
      if (isOnlyPlusOrMinus(val)) return true; // allow "+" or "-" as initial "numeric" input when user types, actual validation will be handled in the next refine
      const num = parseLocaleNumber(val);
      return !isNaN(num);
    }, 'validationErrors.OnlyNumeric')
    .refine((val) => {
      const num = parseLocaleNumber(val);
      return !isNaN(num) && num > 0;
    }, 'validationErrors.PositiveNumber');
};

/**
 * Validates an optional string input as a positive number (> 0).
 * Supports locale-aware decimal notation (comma or period separator). Allows empty values.
 */
export const optionalPositiveNumber = () => {
  return z
    .string()
    .trim()
    .refine((val) => {
      if (isEmptyOrOnlyPlusOrMinus(val)) return true; // allow empty value and "+" or "-" as initial "numeric" input when user types
      const num = parseLocaleNumber(val);
      return !isNaN(num);
    }, 'validationErrors.OnlyNumeric')
    .optional()
    .refine((val) => {
      if (!val) return true; // allow empty value
      const num = parseLocaleNumber(val);
      return !isNaN(num) && num > 0;
    }, 'validationErrors.PositiveNumber');
};

/**
 * Validates a required string input as a non-negative number (>= 0).
 * Supports locale-aware decimal notation (comma or period separator).
 */
export const requiredNonNegativeNumber = () => {
  return z
    .string()
    .min(1, 'validationErrors.Required')
    .refine((val) => {
      if (isOnlyPlusOrMinus(val)) return true; // allow "+" or "-" as initial "numeric" input when user types, actual validation will be handled in the next refine
      const num = parseLocaleNumber(val);
      return !isNaN(num);
    }, 'validationErrors.OnlyNumeric')
    .refine((val) => {
      const num = parseLocaleNumber(val);
      return !isNaN(num) && num >= 0;
    }, 'validationErrors.NonNegativeNumber');
};

/**
 * Validates an optional string input as a non-negative number (>= 0).
 * Supports locale-aware decimal notation (comma or period separator). Allows empty values.
 */
export const optionalNonNegativeNumber = () => {
  return z
    .string()
    .refine((val) => {
      if (isEmptyOrOnlyPlusOrMinus(val)) return true; // allow empty value and "+" or "-" as initial "numeric" input when user types
      const num = parseLocaleNumber(val);
      return !isNaN(num);
    }, 'validationErrors.OnlyNumeric')
    .optional()
    .refine((val) => {
      if (!val) return true; // allow empty value
      const num = parseLocaleNumber(val);
      return !isNaN(num) && num >= 0;
    }, 'validationErrors.NonNegativeNumber');
};

/**
 * Validates a required string input as any number (positive, negative, or zero).
 * Supports locale-aware decimal notation (comma or period separator).
 */
export const requiredNumber = () => {
  return z
    .string()
    .min(1, 'validationErrors.Required')
    .refine((val) => {
      const num = parseLocaleNumber(val);
      return !isNaN(num);
    }, 'validationErrors.Number');
};

/**
 * Validates an optional string input as any number (positive, negative, or zero).
 * Supports locale-aware decimal notation (comma or period separator). Allows empty values.
 */
export const optionalNumber = () => {
  return z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true; // allow empty value
      const num = parseLocaleNumber(val);
      return !isNaN(num);
    }, 'validationErrors.Number');
};

/**
 * Validates an optional string input as a basic phone number.
 * Supports any non-alphabetic characters (digits, spaces, +, -, parentheses). Allows empty values.
 */
export const optionalBasicPhoneNumber = () => {
  return z
    .string()
    .trim()
    .regex(
      /^$|^[^a-zA-Z]*$/,
      'validationErrors.CannotContainAlphabeticCharacters',
    )
    .optional();
};

/**
 * Validates an optional select option object with `id` and `value` string fields.
 * Supports additional custom fields via the `additionalOptionFields` parameter. Allows null and undefined.
 */
export const optionalSelectOption = <T extends z.ZodRawShape>(
  additionalOptionFields?: T,
) => {
  return z
    .object({
      id: z.string(),
      value: z.string(),
      ...additionalOptionFields,
    })
    .nullable()
    .optional();
};

// TODO: this schema extension is still not fully working in translating to typesense
/**
 * Validates a required select option object with non-empty `id` and `value` string fields.
 * Supports additional custom fields via the `additionalOptionFields` parameter.
 */
export const requiredSelectOption = <T extends z.ZodRawShape>(
  additionalOptionFields?: T,
) => {
  return (
    z
      .object(
        {
          id: z.string(),
          value: z.string(),
        },
        'validationErrors.Required',
      )
      .extend({
        ...additionalOptionFields,
      })
      /*
       * make nullable for allowing initial state and not block refines in
       * full form schemas from running
       */
      .nullable()
      /*
       * Use refine to manually validate the string fields inside the object
       * for replicating required/ non-null-able behavior
       */
      .refine(
        (obj) => obj && obj.id !== '' && obj.value !== '',
        'validationErrors.Required',
      )
  );
};
